import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit3, FiCheckCircle, FiBook, FiTag, FiCalendar, FiLogOut } = FiIcons;

const Dashboard = () => {
  const { projects, markProjectAsPublished } = useProject();
  const { logout } = useAuth();

  const getStatusColor = (status) => {
    const colors = {
      'Drafting': 'bg-yellow-100 text-yellow-800',
      'Editing': 'bg-blue-100 text-blue-800',
      'Setup': 'bg-purple-100 text-purple-800',
      'Publishing Soon': 'bg-green-100 text-green-800',
      'Published': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleMarkAsPublished = (projectId) => {
    markProjectAsPublished(projectId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header with Logout */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-between items-start mb-8">
            <div className="flex-1" />
            <button
              onClick={logout}
              className="bg-white/80 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-lg hover:bg-white transition-colors duration-200 flex items-center text-sm font-medium shadow-sm"
            >
              <SafeIcon icon={FiLogOut} className="mr-2" />
              Sign Out
            </button>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Welcome back to <span className="text-blue-600">GospelWise Publishing!</span>
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Choose a project to continue your publishing setup journey.
          </p>
          <p className="text-lg text-blue-700 font-medium italic">
            "Your publishing progress is part of your faithful stewardship."
          </p>
        </motion.div>

        {/* Add New Project Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link
            to="/project/new"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
          >
            <SafeIcon icon={FiPlus} className="mr-2" />
            Add New Book Project
          </Link>
        </motion.div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <SafeIcon icon={FiBook} className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-gray-600 mb-2">No projects yet</h3>
            <p className="text-gray-500">Create your first book project to get started!</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-all duration-200 hover:transform hover:scale-105"
              >
                {/* Project Header */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                    {project.title}
                  </h3>
                  {project.subtitle && (
                    <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                      {project.subtitle}
                    </p>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                    <span className="text-sm text-gray-500">
                      {project.genre}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm font-medium text-gray-700">{project.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="space-y-2 mb-6 text-sm text-gray-600">
                  {project.secondary_category && (
                    <div className="flex items-center">
                      <SafeIcon icon={FiTag} className="mr-2 text-blue-500" />
                      <span>{project.secondary_category}</span>
                    </div>
                  )}
                  {project.target_release_date && (
                    <div className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="mr-2 text-gray-400" />
                      <span>Target: {new Date(project.target_release_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {project.word_count && (
                    <div className="flex items-center">
                      <SafeIcon icon={FiBook} className="mr-2 text-gray-400" />
                      <span>{project.word_count.toLocaleString()} words</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <Link
                    to={`/project/${project.id}/checklist`}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 px-4 rounded-md hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center font-medium"
                  >
                    <SafeIcon icon={FiCheckCircle} className="mr-2" />
                    Open Checklist
                  </Link>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/project/${project.id}/edit`}
                      className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center text-sm"
                    >
                      <SafeIcon icon={FiEdit3} className="mr-1" />
                      Edit Info
                    </Link>
                    
                    {project.status !== 'Published' && (
                      <button
                        onClick={() => handleMarkAsPublished(project.id)}
                        className="flex-1 bg-orange-100 text-orange-700 py-2 px-4 rounded-md hover:bg-orange-200 transition-colors duration-200 text-sm font-medium"
                      >
                        Mark Published
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Inspirational Footer */}
        {projects.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12 p-6 bg-white/50 rounded-lg"
          >
            <p className="text-lg text-blue-700 font-medium italic">
              "Which book are you preparing today?"
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;